'use client'

import { Button } from '@/components/ui/button'
import { FormError, FormLabel } from '@/components/ui/form'
import { URLPreviewCard } from '@/components/url-preview'
import { cn } from '@/lib/utils'
import { formatFileSize, getFileIcon } from '@/utils/file-utils'
import { Cloud, X } from 'lucide-react'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'

export interface FileUploadProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  required?: boolean
  error?: string
  helpertext?: string
  info?: string
  multiple?: boolean
  accept?: string
  maxSize?: number // in bytes
  disabled?: boolean
  className?: string
  onDelete?: () => Promise<void>
  deleting?: boolean
  variant?: 'sm' | 'md'
  /** Files already chosen by the parent. Shown again when this control remounts. */
  defaultFiles?: File[]
}

const getFileIconComponent = (file: File) => {
  const FileIcon = getFileIcon(file.type)
  return <FileIcon className='h-6 w-6 text-gray-500' />
}

const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
  (
    {
      label,
      required,
      error,
      helpertext,
      info,
      multiple = false,
      accept,
      maxSize,
      disabled = false,
      className = '',
      onChange,
      value,
      onDelete,
      deleting,
      variant = 'md',
      defaultFiles,
      ...props
    },
    ref
  ) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [isDragOver, setIsDragOver] = useState(false)
    const [selectedFiles, setSelectedFiles] = useState<File[]>(defaultFiles ?? [])
    // Why a file the user picked was rejected (too large / wrong type). Shown in place of
    // the form error, since the rejected file never reaches the form validation.
    const [rejectionError, setRejectionError] = useState<string | null>(null)
    const fileInputRef = React.useRef<HTMLInputElement | null>(null)

    const displayError = rejectionError ?? error

    // Bridge parent ref (react-hook-form) to the real input element
    const setInputRef = useCallback(
      (node: HTMLInputElement | null) => {
        fileInputRef.current = node
        if (!ref) return
        if (typeof ref === 'function') {
          ref(node)
        } else {
          ;(ref as React.MutableRefObject<HTMLInputElement | null>).current = node
        }
      },
      [ref]
    )

    const validateFile = useCallback(
      (file: File): { valid: boolean; error?: string } => {
        if (maxSize && file.size > maxSize) {
          return {
            valid: false,
            error: `is ${formatFileSize(file.size)}, which exceeds the maximum size of ${formatFileSize(maxSize)}`,
          }
        }
        if (accept) {
          const acceptedTypes = accept.split(',').map((type) => type.trim())
          const fileExtension = file.name.split('.').pop()?.toLowerCase()
          const fileMimeType = file.type
          const isAccepted = acceptedTypes.some((type) => {
            if (type.startsWith('.')) return fileExtension === type.substring(1)
            if (type.includes('*')) return fileMimeType.startsWith(type.split('/')[0] + '/')
            return fileMimeType === type || fileExtension === type
          })
          if (!isAccepted) return { valid: false, error: `is not a supported file type. Accepted types: ${accept}` }
        }
        return { valid: true }
      },
      [accept, maxSize]
    )

    const programmaticallySetFiles = useCallback(
      (files: File[]) => {
        const input = fileInputRef.current
        if (!input) return
        if (files.length === 0) {
          input.value = ''
        } else {
          const dt = new DataTransfer()
          files.forEach((f) => dt.items.add(f))
          input.files = dt.files
        }
        // Inform react-hook-form
        if (onChange) onChange({ target: input } as unknown as React.ChangeEvent<HTMLInputElement>)
      },
      [onChange]
    )

    const handleFileChange = useCallback(
      (filesList: FileList | null) => {
        if (!filesList || filesList.length === 0) {
          setSelectedFiles([])
          setRejectionError(null)
          programmaticallySetFiles([])
          return
        }
        const files = Array.from(filesList)
        const validFiles: File[] = []
        const rejections: string[] = []
        files.forEach((file) => {
          const validation = validateFile(file)
          if (validation.valid) validFiles.push(file)
          else rejections.push(`"${file.name}" ${validation.error}.`)
        })

        // Rejected files are never handed to the form, so surface the reason here -
        // otherwise the selection silently does nothing.
        if (rejections.length > 0) {
          const message = rejections.join(' ')
          setRejectionError(message)
        } else {
          setRejectionError(null)
        }

        setSelectedFiles(validFiles)
        programmaticallySetFiles(validFiles)
      },
      [programmaticallySetFiles, validateFile]
    )

    const handleFileSelect = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        handleFileChange(event.target.files)
        // Also pass through to parent onChange (react-hook-form) to be extra safe
        onChange?.(event)
        setPreviewUrl(null)
      },
      [handleFileChange, onChange]
    )

    const handleDragOver = useCallback((event: React.DragEvent) => {
      // CRITICAL: preventDefault() is required for drop to work
      event.preventDefault()
      event.stopPropagation()

      // Check if there are file items - this is the most reliable indicator
      const items = Array.from(event.dataTransfer.items)
      const hasFileItems = items.some((item) => item.kind === 'file')

      // Also check for 'Files' type (standard browser indicator)
      const types = Array.from(event.dataTransfer.types)
      const hasFilesType = types.includes('Files')

      // Allow drop if we detect file items or Files type
      // This works for ALL file types: images, PDF, DOC, CSV, HTML, etc.
      if (hasFileItems || hasFilesType) {
        event.dataTransfer.dropEffect = 'copy'
        setIsDragOver(true)
      } else {
        event.dataTransfer.dropEffect = 'none'
        setIsDragOver(false)
      }
    }, [])

    const handleDragEnter = useCallback((event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()

      // Check if there are file items - this is the most reliable indicator
      const items = Array.from(event.dataTransfer.items)
      const hasFileItems = items.some((item) => item.kind === 'file')

      // Also check for 'Files' type (standard browser indicator)
      const types = Array.from(event.dataTransfer.types)
      const hasFilesType = types.includes('Files')

      // Allow drop if we detect file items or Files type
      if (hasFileItems || hasFilesType) {
        event.dataTransfer.dropEffect = 'copy'
        setIsDragOver(true)
      }
    }, [])

    const handleDragLeave = useCallback((event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()

      // Only set drag over to false if we're actually leaving the drop zone
      // (not just moving between child elements)
      const currentTarget = event.currentTarget as HTMLElement
      const relatedTarget = event.relatedTarget as HTMLElement | null

      // Check if we're leaving the drop zone (not just moving to a child element)
      if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
        setIsDragOver(false)
      }
    }, [])

    const handleDrop = useCallback(
      (event: React.DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        setIsDragOver(false)

        // Extract files - try multiple methods to ensure we get all file types
        const fileList: File[] = []

        // Method 1: Get files directly from dataTransfer.files
        const directFiles = event.dataTransfer.files
        if (directFiles && directFiles.length > 0) {
          fileList.push(...Array.from(directFiles))
        }

        // Method 2: Extract from dataTransfer.items (critical for CSV, HTML, and some browsers)
        const items = Array.from(event.dataTransfer.items)
        items.forEach((item) => {
          if (item.kind === 'file') {
            const file = item.getAsFile()
            if (file) {
              // Avoid duplicates - check if file already exists
              const exists = fileList.some(
                (f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified
              )
              if (!exists) {
                fileList.push(file)
              }
            }
          }
        })

        // Process files if we have any
        if (fileList.length > 0) {
          // Create a FileList using DataTransfer
          const dt = new DataTransfer()
          fileList.forEach((f) => dt.items.add(f))
          handleFileChange(dt.files)
        }
      },
      [handleFileChange]
    )

    const removeFile = useCallback(
      (index: number) => {
        const next = selectedFiles.filter((_, i) => i !== index)
        setSelectedFiles(next)
        setRejectionError(null)
        programmaticallySetFiles(next)
      },
      [programmaticallySetFiles, selectedFiles]
    )

    useEffect(() => {
      if (value && typeof value === 'string' && value.includes('blob.core.windows.net')) {
        // set null in register  {...register('logo')}
        handleFileSelect({ target: { files: [] } } as unknown as React.ChangeEvent<HTMLInputElement>)
        setPreviewUrl(value)
      }
    }, [value])

    return (
      <div className={className}>
        {label && (
          <FormLabel required={required} info={info}>
            {label}
          </FormLabel>
        )}

        <div className='space-y-4'>
          {/* Hidden file input */}
          <input
            ref={setInputRef}
            type='file'
            multiple={multiple}
            accept={accept}
            onChange={handleFileSelect}
            className='sr-only'
            disabled={disabled}
            {...props}
          />

          {/* Drag & Drop Zone */}
          <div
            className={cn(
              `relative  border-dashed rounded-lg transition-colors 
              ${isDragOver ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20' : 'border-gray-300 dark:border-gray-600'} 
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${displayError ? 'border-red-500' : ''}
              ${variant === 'sm' ? 'p-3 border-1' : 'p-6 border-2'}
              `
            )}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !disabled && fileInputRef.current?.click()}
          >
            <div className='text-center'>
              <div className={cn('flex justify-center', variant === 'sm' ? 'mb-2' : 'mb-4')}>
                <div
                  className={cn(`p-3 rounded-full ${isDragOver ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-gray-800'}
                    `)}
                >
                  <Cloud className={cn('text-gray-500', variant === 'sm' ? 'h-4 w-4' : 'h-8 w-8')} />
                </div>
              </div>
              <div className='space-y-2'>
                <p
                  className={cn(
                    'font-medium text-gray-900 dark:text-gray-100',
                    variant === 'sm' ? 'text-sm' : 'text-lg'
                  )}
                >
                  {isDragOver ? 'Drop files here' : 'Drag & drop files here'}
                </p>
                {variant === 'sm' ? null : (
                  <p className={cn('text-gray-500 dark:text-gray-400 text-sm')}>or click to browse</p>
                )}
                {accept && (
                  <p className='text-xs text-gray-400 dark:text-gray-500 break-words'>Accepted formats: {accept}</p>
                )}
                {maxSize && (
                  <p className='text-xs text-gray-400 dark:text-gray-500'>Max size: {formatFileSize(maxSize)}</p>
                )}
              </div>
            </div>
          </div>

          <div className='space-y-1'>
            {helpertext && <p className='text-sm text-gray-500 dark:text-gray-400'>{helpertext}</p>}
            <FormError error={displayError} />
          </div>

          {selectedFiles.length > 0 && (
            <div className='space-y-2'>
              {selectedFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg'
                >
                  <div className='flex items-center space-x-3 flex-1 min-w-0'>
                    <div className='flex-shrink-0'>
                      <div className='h-8 w-8 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center'>
                        {getFileIconComponent(file)}
                      </div>
                    </div>
                    <div className='flex-1 min-w-0'>
                      <p className='text-sm font-medium text-gray-900 dark:text-gray-100 truncate'>{file.name}</p>
                      <p className='text-xs text-gray-500 dark:text-gray-400'>{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={(e) => {
                      e.stopPropagation()
                      removeFile(index)
                    }}
                    disabled={disabled}
                    className='flex-shrink-0'
                  >
                    <X className='h-4 w-4' />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {previewUrl ? (
            <URLPreviewCard
              url={previewUrl}
              onDelete={onDelete}
              deleting={deleting}
              hideRemoveButton={onDelete ? false : true}
            />
          ) : null}
        </div>
      </div>
    )
  }
)

FileUpload.displayName = 'FileUpload'

export { FileUpload }
